// Canonical job-type colour scheme (LOCKED -- see CLAUDE.md house rules). This is
// the single source for how a job's type is coloured and labelled on every surface
// that shows one (the dispatch board, job sheets, the engineer PWA and the public
// RFID page). Never inline these per file.
//
//   PPM / service visits    -> ele-green   (#63C41F)
//   LOLER inspection visits -> ele-loler   (#2CB9F3)  (a dedicated token, NOT the
//                                                      brand --ele-blue #00C4FF)
//   Emergency call-out      -> ele-danger  (red)
//   Remedial / follow-up    -> amber
//
// The Prisma JobType enum is service | callout | loler | repair, plus the two OBSERVATIONAL types
// Session ALW added (condition_report and site_inspection) and the tech visit Session ANG added.
// Call-outs take the emergency (red) treatment; repairs AND tech visits are the remedial /
// follow-up (amber) work -- see the tech_visit entry below for why they share a colour.
//
// THE TWO OBSERVATIONAL TYPES TAKE PURPLE, and that is a deliberate reading of the locked scheme
// rather than a new colour. The four locked job-type colours each mean something specific about
// WORK ON A LIFT (green = the contract is being serviced, blue = a statutory examination, red = a
// fault, amber = remedial work outstanding), and neither new type is any of those: nobody is
// fixing, servicing or examining anything, and colouring an inspection amber would put a "remedial
// work outstanding" signal on a lift where none has been found. Purple is already the platform's
// structural brand colour and is ALREADY the PWA's non-job accent (the training card uses it for
// exactly this reason: "so it never reads as a job status"). It introduces no new hex: #7d21c7 is
// the locked brand purple. The two share the colour and are told apart by their LABELS, which is
// the same distinction the scheme already makes between a service and an equipment service.
//
// Colours are Tailwind utility classes that resolve to the brand tokens (never raw
// hex -- see tailwind.config.cjs). The literal class strings live here so Tailwind's
// content scan keeps them in the build.

import { CalendarCheck, ClipboardCheck, ClipboardList, Eye, Gauge, Hammer, ListChecks, Ruler, ShieldCheck, Siren, Wrench, type LucideIcon } from "lucide-react";

export type JobTypeKey = "service" | "callout" | "loler" | "repair" | "condition_report" | "site_inspection" | "tech_visit" | "loler_follow_up" | "insurance_check" | "safed_test" | "project_works" | "project_survey";

export interface JobTypeStyle {
  /** Plain-English label for office/engineer surfaces. */
  label: string;
  /** Text colour utility (e.g. for a label). */
  text: string;
  /** Border colour utility (e.g. for a card). */
  border: string;
  /** Solid background utility (e.g. for a badge or a status dot). */
  bg: string;
  /** Readable text colour to sit on top of `bg`. */
  onBg: string;
}

const JOB_TYPE_STYLE: Record<JobTypeKey, JobTypeStyle> = {
  service: { label: "Service", text: "text-ele-green", border: "border-ele-green", bg: "bg-ele-green", onBg: "text-ele-bg" },
  loler: { label: "LOLER inspection", text: "text-ele-loler", border: "border-ele-loler", bg: "bg-ele-loler", onBg: "text-ele-bg" },
  callout: { label: "Call-out", text: "text-ele-danger", border: "border-ele-danger", bg: "bg-ele-danger", onBg: "text-ele-white" },
  repair: { label: "Repair", text: "text-amber-400", border: "border-amber-400", bg: "bg-amber-400", onBg: "text-ele-bg" },
  // Session ALW. onBg is white on both: #7d21c7 measures 7.2:1 against white, so white text on a
  // purple fill clears WCAG AA comfortably (the CTA / bright-fill rule in brand.md -- it is green
  // and blue fills that need dark ink, not purple).
  condition_report: { label: "Condition report", text: "text-ele-purple", border: "border-ele-purple", bg: "bg-ele-purple", onBg: "text-ele-white" },
  site_inspection: { label: "Site inspection", text: "text-ele-purple", border: "border-ele-purple", bg: "bg-ele-purple", onBg: "text-ele-white" },
  // Session ANG. A tech visit takes the SAME amber as a repair, and that is a reading of the locked
  // scheme rather than an omission. Amber's locked meaning is "remedial / follow-up", and a tech
  // visit is exactly that: the return visit a call-out diagnosed as needing more than routine repair
  // work. It is not green (nothing is being serviced), not blue (no statutory examination), not red
  // (the fault is no longer the live emergency the call-out was), and not the observational purple
  // (real work is being done, not looked at). Inventing a FIFTH job-type colour would be a brand
  // decision, and the locked scheme names four; sharing one and telling the two apart by LABEL is
  // the distinction Session ALW already made for the two purple types, and the one the scheme
  // itself makes between a service and an equipment service.
  // Session BAQ. The examiner's LOLER items on a lift: LOLER blue, told apart from the examination itself by LABEL.
  loler_follow_up: { label: "LOLER items", text: "text-ele-loler", border: "border-ele-loler", bg: "bg-ele-loler", onBg: "text-ele-bg" },
  // Session BBF. An INSURANCE CHECK takes the observational purple, like a site inspection, and for the same reason: the
  // engineer is LOOKING at what a third party reported, not servicing, examining or fixing anything, and an amber pill would
  // put "remedial work outstanding" on a lift where nothing has been found yet. Told apart by LABEL.
  insurance_check: { label: "Insurance check", text: "text-ele-purple", border: "border-ele-purple", bg: "bg-ele-purple", onBg: "text-ele-white" },
  // Session BDA. A SAFed supplementary test. WORKING COLOUR: the LOLER token (ele-loler, #2CB9F3), named in this one line so the
  // Director can change it here alone (alternatives: brand purple, crowded by three types already, or a new hex which is his
  // decision). Told apart from LOLER inspection and LOLER items by LABEL and the Gauge icon.
  safed_test: { label: "SAFed test", text: "text-ele-loler", border: "border-ele-loler", bg: "bg-ele-loler", onBg: "text-ele-bg" },
  // Session BDM. The WORKS JOB of a project (a modernisation, an installation, major remedial work) takes the remedial amber:
  // it is booked work being done, shares the repair colour as a tech visit does, and is told apart by LABEL and icon.
  project_works: { label: "Project works", text: "text-amber-400", border: "border-amber-400", bg: "bg-amber-400", onBg: "text-ele-bg" },
  // Session PJ2. A project SURVEY visit (a lift-by-lift measure-up before a modernisation or new lift) takes the same remedial amber as the
  // project works job, told apart by LABEL and the Ruler icon.
  project_survey: { label: "Project survey", text: "text-amber-400", border: "border-amber-400", bg: "bg-amber-400", onBg: "text-ele-bg" },
  tech_visit: { label: "Tech visit", text: "text-amber-400", border: "border-amber-400", bg: "bg-amber-400", onBg: "text-ele-bg" },
};

const FALLBACK: JobTypeStyle = JOB_TYPE_STYLE.service;

/** Every job type key, in the order the style table lists them (Session SC7A: the All jobs filter reads this, so no page names a type twice). */
export const JOB_TYPE_KEYS = Object.keys(JOB_TYPE_STYLE) as JobTypeKey[];

export function jobTypeStyle(type: string): JobTypeStyle {
  return JOB_TYPE_STYLE[type as JobTypeKey] ?? FALLBACK;
}

export function jobTypeLabel(type: string): string {
  return jobTypeStyle(type).label;
}

// ---- Session ANG: REMEDIAL WORK, the client half -------------------------------------------
//
// The mirror of isRemedialJobType in server/src/modules/jobsheets/fieldLoop.ts, bound to it by a
// parity test (server/src/modules/jobsheets/remedialJobTypeParity.test.ts) exactly as the
// service-duration rule's two homes are bound by durationParity.test.ts. It exists twice for the same reason the job-type LABELS do: no
// server production file imports from this client tree, and no client file imports from the server.
//
// A REPAIR and a TECH VISIT are the same kind of work to everyone except ELE's own accounts: both
// are ad-hoc remedial visits with no maintenance checklist, both take the call-out-style capture
// form on the engineer's phone, and both produce the same customer document. What separates them is
// the hourly rate ELE pays for one, which is an internal fact. Ask this question rather than
// `type === "repair"` wherever the distinction being drawn is "is this remedial work"; ask
// `type === "tech_visit"` only where the RATE or the classification is genuinely the point.
export const REMEDIAL_JOB_TYPES = ["repair", "tech_visit"] as const;

export function isRemedialJobType(type: string | null | undefined): boolean {
  return type != null && (REMEDIAL_JOB_TYPES as readonly string[]).includes(type);
}

// The NOUN for a remedial visit, as ELE's own people say it: "repair" / "tech visit". The mirror of
// remedialJobNoun in server/src/modules/jobsheets/fieldLoop.ts, bound to it by the same parity test.
//
// DELIBERATELY NOT jobTypeLabel. That returns the colour-scheme label, which for a tech visit is
// "Tech visit" -- correct on a status pill, wrong in a sentence an engineer reads ("Submit tech visit"
// where the form is the repair form's). Reaching for jobTypeLabel here silently renamed the repair form on the
// engineer's phone, caught by two existing tests that had pinned the real wording for two sessions.
const REMEDIAL_NOUN: Record<(typeof REMEDIAL_JOB_TYPES)[number], string> = {
  repair: "repair",
  tech_visit: "tech visit",
};

export function remedialJobNoun(type: string | null | undefined): string {
  return REMEDIAL_NOUN[type as (typeof REMEDIAL_JOB_TYPES)[number]] ?? "repair";
}

// Session ACR: a SERVICE job on a piece of care equipment keeps the locked service colours
// but is labelled "Equipment service" (a label/badge distinction, never a new colour --
// the locked scheme above is untouched). Every other type/kind passes through unchanged.
export function jobTypeStyleFor(type: string, opts?: { equipmentAsset?: boolean }): JobTypeStyle {
  const base = jobTypeStyle(type);
  if (opts?.equipmentAsset && type === "service") return { ...base, label: "Equipment service" };
  return base;
}

// ---- Job type clarity: the ICON and the short PLAIN label, beside the locked colours ---------
//
// Colour is never the only signal: every surface that shows a job's type shows its icon AND its
// label (JobTypeBadge, src/components/JobTypeBadge.tsx). Repair and tech visit share amber, so they
// take different icons (hammer / spanner); the two purple observational types differ the same way.
// The colours above are untouched.
export const JOB_TYPE_ICON: Record<JobTypeKey, LucideIcon> = {
  service: CalendarCheck,
  repair: Hammer,
  callout: Siren,
  loler: ShieldCheck,
  tech_visit: Wrench,
  condition_report: ClipboardList,
  site_inspection: Eye,
  loler_follow_up: ListChecks,
  insurance_check: ClipboardCheck,
  safed_test: Gauge,
  project_works: Hammer,
  project_survey: Ruler,
};

export function jobTypeIcon(type: string): LucideIcon {
  return JOB_TYPE_ICON[type as JobTypeKey] ?? CalendarCheck;
}

// The label for the BADGE. A repair is "Repair" everywhere since Session BBA (the Director retired
// "Remedial" as a type label); the explicit branch stays so a badge can never drift from that.
// An equipment service keeps "Equipment service".
export function jobTypeBadgeLabel(type: string, opts?: { equipmentAsset?: boolean }): string {
  if (type === "repair") return "Repair";
  return jobTypeStyleFor(type, opts).label;
}

// The soft wash a card takes from its type (a theme-aware pill tint, never a left stripe). Pairs with
// the type's border; the tints are the platform's own tokens, so no new colour exists.
const JOB_TYPE_TINT: Record<JobTypeKey, string> = {
  service: "var(--ele-pill-tint-green)",
  loler: "var(--ele-pill-tint-loler)",
  loler_follow_up: "var(--ele-pill-tint-loler)",
  safed_test: "var(--ele-pill-tint-loler)",
  insurance_check: "var(--ele-pill-tint-purple)",
  callout: "var(--ele-pill-tint-red)",
  repair: "var(--ele-pill-tint-amber)",
  tech_visit: "var(--ele-pill-tint-amber)",
  project_works: "var(--ele-pill-tint-amber)",
  project_survey: "var(--ele-pill-tint-amber)",
  condition_report: "var(--ele-pill-tint-purple)",
  site_inspection: "var(--ele-pill-tint-purple)",
};
export function jobTypeTint(type: string): string {
  return JOB_TYPE_TINT[type as JobTypeKey] ?? JOB_TYPE_TINT.service;
}
